import { useCallback, useEffect, useMemo, useState } from 'react';
import { EVENT_CATEGORIES } from '../constants/eventCategories';
import { getWallCache, saveWallList } from '../utils/wallCache';

/**
 * The events wall's filters — When · Type · Category · Cities — lifted out of AllEventsGrid so
 * the FILTERS drawer can live at page level and the same selection narrows Featured as well as
 * All Events (docs: events-wall handoff §2).
 *
 * Nothing about how they persist changed: category rides the URL (`?category=`, owned by the
 * page), cities ride localStorage, and type / days / search ride the wall's back-nav cache so a
 * return lands on the same filtered view.
 */
const CITY_STORAGE_KEY = 'pr.allEvents.selectedCities';

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const sameSet = (a, b) => a.length === b.length && a.every((x) => b.includes(x));
/** "Naramata, BC" and "Naramata" are one city, matching the server-side filter. */
export const cityOf = (locationCity) => (locationCity || '').split(',')[0].trim();

export default function useWallFilters({ restoreFromBack = false, category = '', onCategoryChange, cityPool = [] } = {}) {
  const [backCache] = useState(() => (restoreFromBack ? getWallCache() : null));

  const [fundingFilter, setFundingFilter] = useState(() => (backCache && backCache.fundingFilter) || 'all');
  const [selectedDays, setSelectedDays] = useState(() => (backCache && Array.isArray(backCache.days) ? backCache.days : []));
  const [search] = useState(() => (backCache && backCache.search) || '');
  const [selectedCities, setSelectedCities] = useState(() => {
    try {
      const raw = localStorage.getItem(CITY_STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : [];
    } catch { return []; }
  });

  // Today + the next six days, each a specific local date.
  const week = useMemo(() => {
    const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return { iso: isoOf(d), dow: DOW[d.getDay()], dayNum: d.getDate(), weekday: d.getDay() };
    });
  }, []);
  const todayIso = week[0].iso;
  const weekendDays = useMemo(() => week.filter((d) => [5, 6, 0].includes(d.weekday)).map((d) => d.iso), [week]);
  const todayActive = sameSet(selectedDays, [todayIso]);
  const weekendActive = weekendDays.length > 0 && sameSet(selectedDays, weekendDays);

  const toggleDay = useCallback((iso) => setSelectedDays((prev) => (prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso])), []);
  const setToday = useCallback(() => setSelectedDays((prev) => (sameSet(prev, [todayIso]) ? [] : [todayIso])), [todayIso]);
  const setWeekend = useCallback(() => setSelectedDays((prev) => (sameSet(prev, weekendDays) ? [] : weekendDays)), [weekendDays]);
  // Date-range picker: a range fills in every calendar day in it, which the server matches from
  // 12 AM of the first through 11:59 PM of the last.
  const pickRange = useCallback((startIso, endIso) => {
    const out = [];
    const d = new Date(`${startIso}T00:00:00`);
    const end = new Date(`${endIso}T00:00:00`);
    while (d <= end) { out.push(isoOf(d)); d.setDate(d.getDate() + 1); }
    setSelectedDays(out);
  }, []);

  // Cities come from what the wall actually pulled for this location (~50 mi).
  const cities = useMemo(() => {
    const m = new Map();
    for (const e of cityPool) {
      const c = cityOf(e.locationCity);
      if (c) m.set(c.toLowerCase(), c);
    }
    return Array.from(m.values()).sort((a, b) => a.localeCompare(b));
  }, [cityPool]);

  // Drop selections that no longer exist after a location change — but not before anything has
  // loaded, or the restored selection would be wiped before it can be matched.
  useEffect(() => {
    if (cities.length === 0) return;
    setSelectedCities((prev) => prev.filter((c) => cities.some((x) => x.toLowerCase() === c.toLowerCase())));
  }, [cities]);
  useEffect(() => {
    try { localStorage.setItem(CITY_STORAGE_KEY, JSON.stringify(selectedCities)); } catch { /* storage disabled */ }
  }, [selectedCities]);
  const toggleCity = useCallback((c) => setSelectedCities((prev) => (
    prev.some((x) => x.toLowerCase() === c.toLowerCase())
      ? prev.filter((x) => x.toLowerCase() !== c.toLowerCase())
      : [...prev, c])), []);

  useEffect(() => { saveWallList({ fundingFilter }); }, [fundingFilter]);
  useEffect(() => { saveWallList({ days: selectedDays }); }, [selectedDays]);

  // `category` is a comma-separated slug list; a multi-select set.
  const activeCats = useMemo(() => new Set((category || '').split(',').map((s) => s.trim()).filter(Boolean)), [category]);
  const toggleCategory = useCallback((slug) => {
    if (!slug) { onCategoryChange?.(null); return; }
    const next = new Set(activeCats);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    onCategoryChange?.(next.size ? [...next].join(',') : null);
  }, [activeCats, onCategoryChange]);

  const groups = {
    when: selectedDays.length > 0,
    type: fundingFilter !== 'all',
    category: activeCats.size > 0,
    cities: selectedCities.length > 0,
  };
  const activeCount = Object.values(groups).filter(Boolean).length;

  const clearAll = useCallback(() => {
    setSelectedDays([]);
    setFundingFilter('all');
    onCategoryChange?.(null);
    setSelectedCities([]);
  }, [onCategoryChange]);

  // Pill labels: the active value, or the filter's name.
  const fmtShort = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const sortedDays = selectedDays.slice().sort();
  const labels = {
    when: selectedDays.length === 0 ? 'When'
      : todayActive ? 'Today'
        : weekendActive ? 'Weekend'
          : selectedDays.length === 1 ? fmtShort(sortedDays[0])
            : `${fmtShort(sortedDays[0])}–${fmtShort(sortedDays[sortedDays.length - 1])}`,
    type: fundingFilter === 'guaranteed' ? 'Guaranteed' : fundingFilter === 'party_starter' ? 'Party Starter' : 'Type',
    category: activeCats.size === 0 ? 'Category'
      : activeCats.size === 1 ? (EVENT_CATEGORIES.find((c) => c.slug === [...activeCats][0])?.label || 'Category')
        : `${activeCats.size} categories`,
    cities: selectedCities.length === 1 ? selectedCities[0] : selectedCities.length > 1 ? `${selectedCities.length} cities` : 'Cities',
  };

  /**
   * The same rules the server applies to All Events, for the lists it doesn't filter — Featured
   * comes down in the wall snapshot, unfiltered, and has to agree with the grid below it.
   */
  const matches = useCallback((e) => {
    if (fundingFilter !== 'all' && (e.fundingType || 'guaranteed') !== fundingFilter) return false;
    if (selectedDays.length && !(e.dateTime && selectedDays.includes(isoOf(new Date(e.dateTime))))) return false;
    if (activeCats.size) {
      const cats = Array.isArray(e.categories) && e.categories.length ? e.categories : [e.category].filter(Boolean);
      if (!cats.some((c) => activeCats.has(c))) return false;
    }
    if (selectedCities.length) {
      const c = cityOf(e.locationCity).toLowerCase();
      if (!selectedCities.some((x) => x.toLowerCase() === c)) return false;
    }
    return true;
  }, [fundingFilter, selectedDays, activeCats, selectedCities]);

  return {
    // state
    fundingFilter, setFundingFilter,
    selectedDays, setSelectedDays, toggleDay, setToday, setWeekend, pickRange,
    selectedCities, setSelectedCities, toggleCity,
    category: category || '', activeCats, toggleCategory,
    search,
    // derived
    week, todayIso, todayActive, weekendActive, cities,
    groups, activeCount, anyActive: activeCount > 0, labels,
    clearAll, matches,
  };
}
